import React from 'react'
import QuestionCard from './QuestionCard'
import RadioOption from './RadioOption'
import CheckboxOption from './CheckboxOption'

interface SectionProps {
  formData: Record<string, any>
  updateField: (field: string, value: any) => void
}

/**
 * Section H — Canais e Operacoes Digitais (Q23-Q28). Shown to all respondents.
 * Covers content management across platforms, brand consistency, live commerce,
 * creator/affiliate relationships, manual activities, and automation impact areas.
 */
const SectionH: React.FC<SectionProps> = ({ formData, updateField }) => {
  const handleCheckboxChange = (field: string, value: string, checked: boolean) => {
    const current: string[] = formData[field] || []
    const updated = checked
      ? [...current, value]
      : current.filter((v: string) => v !== value)
    updateField(field, updated)
  }

  return (
    <div className="flex flex-col gap-6">

      {/* Q23 — Gestao de conteudo multiplataforma */}
      <QuestionCard
        title="Como você gerencia seu conteúdo em múltiplas plataformas hoje?"
        required
      >
        <RadioOption
          name="q23"
          value="totalmente_manual"
          label="Totalmente manual"
          selected={formData.q23 === 'totalmente_manual'}
          onChange={(v) => updateField('q23', v)}
        />
        <RadioOption
          name="q23"
          value="parcialmente_sistematizado"
          label="Parcialmente sistematizado"
          selected={formData.q23 === 'parcialmente_sistematizado'}
          onChange={(v) => updateField('q23', v)}
        />
        <RadioOption
          name="q23"
          value="totalmente_sistematizado"
          label="Totalmente sistematizado"
          selected={formData.q23 === 'totalmente_sistematizado'}
          onChange={(v) => updateField('q23', v)}
        />
      </QuestionCard>

      {/* Q24 — Consistencia entre marca, criadores e anuncios */}
      <QuestionCard
        title="Como você garante consistência entre conteúdo da marca, criadores/afiliados e anúncios pagos?"
        required
      >
        <RadioOption
          name="q24"
          value="nao_trabalhamos"
          label="Não trabalhamos com criadores/afiliados"
          selected={formData.q24 === 'nao_trabalhamos'}
          onChange={(v) => updateField('q24', v)}
        />
        <RadioOption
          name="q24"
          value="nao_garantimos"
          label="Não garantimos consistência"
          selected={formData.q24 === 'nao_garantimos'}
          onChange={(v) => updateField('q24', v)}
        />
        <RadioOption
          name="q24"
          value="manualmente"
          label="Manualmente"
          selected={formData.q24 === 'manualmente'}
          onChange={(v) => updateField('q24', v)}
        />
        <RadioOption
          name="q24"
          value="sistema_estruturado"
          label="Sistema estruturado"
          selected={formData.q24 === 'sistema_estruturado'}
          onChange={(v) => updateField('q24', v)}
        />
        <RadioOption
          name="q24"
          value="automatizado"
          label="Automatizado"
          selected={formData.q24 === 'automatizado'}
          onChange={(v) => updateField('q24', v)}
        />
      </QuestionCard>

      {/* Q25 — Live commerce */}
      <QuestionCard
        title="Como você gerencia live commerce hoje?"
        required
      >
        <RadioOption
          name="q25"
          value="nao_fazemos"
          label="Não fazemos e não temos interesse"
          selected={formData.q25 === 'nao_fazemos'}
          onChange={(v) => updateField('q25', v)}
        />
        <RadioOption
          name="q25"
          value="interesse"
          label="Temos interesse mas ainda não começamos"
          selected={formData.q25 === 'interesse'}
          onChange={(v) => updateField('q25', v)}
        />
        <RadioOption
          name="q25"
          value="testando"
          label="Estamos testando"
          selected={formData.q25 === 'testando'}
          onChange={(v) => updateField('q25', v)}
        />
        <RadioOption
          name="q25"
          value="time_interno"
          label="Time interno"
          selected={formData.q25 === 'time_interno'}
          onChange={(v) => updateField('q25', v)}
        />
        <RadioOption
          name="q25"
          value="agencia"
          label="Agência"
          selected={formData.q25 === 'agencia'}
          onChange={(v) => updateField('q25', v)}
        />
      </QuestionCard>

      {/* Q26 — Relacionamento com criadores/afiliados */}
      <QuestionCard
        title="Como você gerencia relacionamentos com criadores/afiliados?"
        required
      >
        <RadioOption
          name="q26"
          value="nao_trabalhamos"
          label="Não trabalhamos com criadores/afiliados"
          selected={formData.q26 === 'nao_trabalhamos'}
          onChange={(v) => updateField('q26', v)}
        />
        <RadioOption
          name="q26"
          value="manualmente"
          label="Manualmente (WhatsApp / planilhas)"
          selected={formData.q26 === 'manualmente'}
          onChange={(v) => updateField('q26', v)}
        />
        <RadioOption
          name="q26"
          value="ferramentas_plataforma"
          label="Ferramentas de plataforma"
          selected={formData.q26 === 'ferramentas_plataforma'}
          onChange={(v) => updateField('q26', v)}
        />
        <RadioOption
          name="q26"
          value="agencia"
          label="Agência"
          selected={formData.q26 === 'agencia'}
          onChange={(v) => updateField('q26', v)}
        />
        <RadioOption
          name="q26"
          value="sistema_automatizado"
          label="Sistema automatizado"
          selected={formData.q26 === 'sistema_automatizado'}
          onChange={(v) => updateField('q26', v)}
        />
      </QuestionCard>

      {/* Q27 — Atividades manuais */}
      <QuestionCard
        title="Quais dessas atividades ainda são manuais no seu negócio?"
      >
        <CheckboxOption
          name="q27"
          value="publicacao_conteudo"
          label="Publicação de conteúdo"
          checked={(formData.q27 || []).includes('publicacao_conteudo')}
          onChange={(v, c) => handleCheckboxChange('q27', v, c)}
        />
        <CheckboxOption
          name="q27"
          value="outreach_criadores"
          label="Outreach para criadores"
          checked={(formData.q27 || []).includes('outreach_criadores')}
          onChange={(v, c) => handleCheckboxChange('q27', v, c)}
        />
        <CheckboxOption
          name="q27"
          value="relatorios"
          label="Relatórios e reporting"
          checked={(formData.q27 || []).includes('relatorios')}
          onChange={(v, c) => handleCheckboxChange('q27', v, c)}
        />
        <CheckboxOption
          name="q27"
          value="otimizacao_campanhas"
          label="Otimização de campanhas"
          checked={(formData.q27 || []).includes('otimizacao_campanhas')}
          onChange={(v, c) => handleCheckboxChange('q27', v, c)}
        />
        <CheckboxOption
          name="q27"
          value="atualizacao_produtos"
          label="Atualização de produtos"
          checked={(formData.q27 || []).includes('atualizacao_produtos')}
          onChange={(v, c) => handleCheckboxChange('q27', v, c)}
        />
      </QuestionCard>

      {/* Q28 — Impacto da automacao */}
      <QuestionCard
        title="Onde automação teria o maior impacto no seu negócio?"
      >
        <CheckboxOption
          name="q28"
          value="escala_conteudo"
          label="Escala de conteúdo"
          checked={(formData.q28 || []).includes('escala_conteudo')}
          onChange={(v, c) => handleCheckboxChange('q28', v, c)}
        />
        <CheckboxOption
          name="q28"
          value="conversao"
          label="Conversão"
          checked={(formData.q28 || []).includes('conversao')}
          onChange={(v, c) => handleCheckboxChange('q28', v, c)}
        />
        <CheckboxOption
          name="q28"
          value="operacoes"
          label="Operações"
          checked={(formData.q28 || []).includes('operacoes')}
          onChange={(v, c) => handleCheckboxChange('q28', v, c)}
        />
        <CheckboxOption
          name="q28"
          value="gestao_criadores"
          label="Gestão de criadores"
          checked={(formData.q28 || []).includes('gestao_criadores')}
          onChange={(v, c) => handleCheckboxChange('q28', v, c)}
        />
        <CheckboxOption
          name="q28"
          value="midia_paga"
          label="Mídia paga"
          checked={(formData.q28 || []).includes('midia_paga')}
          onChange={(v, c) => handleCheckboxChange('q28', v, c)}
        />
      </QuestionCard>
    </div>
  )
}

export default SectionH
